import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, IconButton, Tooltip, useTheme, useMediaQuery, Card, CardContent, Divider, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar, Alert, CircularProgress, TextField } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Visibility as VisibilityIcon, Delete as DeleteIcon } from "@mui/icons-material";
import PrintIcon from "@mui/icons-material/Print";
import { VscNewFile } from "react-icons/vsc";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import CustomNoDataOverlay from "../../components/CustomNoDataOverlay";
import NoDataComponent from "../../components/NoDataComponent";
import SkeletonLoader from "../../components/SkeletonLoader";
import LoadingOverlay from "../../components/LoadingOverlay";
import { motion } from "framer-motion";


/**
 * Helper function to normalize dates
 * "YYYY-MM-DD" → "DD/MM/YYYY"
 */
const normalizeDate = (v) =>
    typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)
        ? v.split("-").reverse().join("/")
        : v || "-";

/**
 * Helper function to format quantities with 4 decimal places
 */
const formatQty = (val) =>
    Number(val || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    });

/**
 * Helper function to format currency (INR)
 */
const formatINR = (val) =>
    Number(val || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

export default function StoreDetails({ connectionString, pdfString }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isSmall = useMediaQuery(theme.breakpoints.down(500));
    const navigate = useNavigate();

    const { user, selectedCompany } = useAuth();
    console.log("selectedCompany:", selectedCompany);
    const role = user?.UserCategory;

    // PDF loading states
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfLoadingId, setPdfLoadingId] = useState(null);
    const [pdfOverlayVisible, setPdfOverlayVisible] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [searchText, setSearchText] = useState("");

    // Delete dialog states
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

    const companyId = selectedCompany?.CompId;
    const userId = user?.UserId;
    const userCategory = user?.UserCategory;

    const API_URL = `${connectionString}/api`;
    const PDF_API_BASE = pdfString;

    const handleView = (row) => navigate(`/main/store-master-edit/${row.id}`);

    // ─────────────────────────────────────────────────────────────────────────────
    // 🔥 PRODUCTION PDF GENERATION (NEW)
    // ─────────────────────────────────────────────────────────────────────────────
    const handleViewClick = async (prodMastID) => {
        if (!prodMastID) return;

        setPdfLoading(true);
        setPdfLoadingId(prodMastID);
        setPdfOverlayVisible(true);

        const userIdSafe = userId || 'usr1';
        let formatId = 'format_1';

        try {
            // Step 1: Fetch company formats (optional, for custom formatting)
            try {
                const resp = await axios.get(`${API_URL}/companies`, { params: { companyId } });
                let comps = [];
                if (Array.isArray(resp?.data?.companies)) comps = resp.data.companies;
                else if (Array.isArray(resp?.data)) comps = resp.data;
                else if (resp?.data) comps = [resp.data];

                // Look for Production format configuration
                for (const c of comps) {
                    if (Array.isArray(c.formats)) {
                        for (const f of c.formats) {
                            const bn = String(f.BookName || f.bookName || "").trim().toLowerCase();
                            const fn = String(f.FormName || f.formName || "").trim().toLowerCase();
                            if (bn.includes('production') || fn.includes('production')) {
                                const foundBookId = f.BookID ?? f.BookId ?? f.bookId ?? null;
                                if (foundBookId) {
                                    formatId = `format_${foundBookId}`;
                                }
                                break;
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn("Format detection failed, using defaults");
            }

            // Step 2: Fetch company icon/logo
            let imageBase64 = null;
            try {
                const iconRes = await axios.get(`${API_URL}/company/GetIcon/${encodeURIComponent(companyId)}`);
                if (iconRes?.data?.success && iconRes?.data?.imageBase64) {
                    const raw = iconRes.data.imageBase64;
                    imageBase64 = typeof raw === 'string'
                        ? raw.split('base64,').pop()?.replace(/\s/g, '') || null
                        : null;
                }
            } catch (err) {
                console.warn("Failed to fetch company icon:", err);
            }

            // Step 3: Fetch production data (MASTER + DETAILS)
            console.log(`[Production PDF] Fetching data for prodMastID: ${prodMastID}`);
            const productionResp = await axios.get(`${API_URL}/get-production-data/${prodMastID}`, {
                params: { companyId },
                headers: { "Cache-Control": "no-store" }
            });

            

            console.log("[Production PDF] Response:", productionResp);

            const { master, details } = productionResp.data || {};
            if (!master) throw new Error("No master data returned");

            // Step 4: Format production items for PDF
            const items = (details || []).map(d => ({
                ItemCode: d.itemCode || "",
                Description: d.description || "",
                Unit: d.Unit || d.UnitName || "",
                QtyIssue: Number(d.QtyIssue || 0),
                QtyRcd: Number(d.QtyRcd || 0),
                Pkg: Number(d.Pkg || 0),
                Rate: Number(d.Rate || 0),
                Amount: Number(d.Amount || 0),
                Remark: d.Remark || ""
            }));

            // Step 5: Build PDF data payload
            const pdfData = {
                jobCardNo: master.ReceiptNum || "",
                reportDate: master.ReceiptDate ? normalizeDate(master.ReceiptDate) : "",
                masterData: {
                    ReceiptNum: master.ReceiptNum || "",
                    ReceiptDate: master.ReceiptDate ? normalizeDate(master.ReceiptDate) : "",
                    TransType: master.TransType || "Production",
                    BookName: master.BookName || "Production",
                    ReceivedBy: master.ReceivedBy || "",
                    IssueBy: master.IssueBy || "",
                    companyName: master.FirmName || "",
                    clientAddress: master.clientAddress || "",
                    clientState: master.clientState || "",
                    clientStateCode: master.clientStateCode || "",
                    clientGSTNo: master.clientGSTNo || "",
                },
                entries: items,
                imageBase64: imageBase64 || null,
            };

            console.log("data:", pdfData)

            // Step 6: Build company header
            const companyHeader = selectedCompany ? {
                CompId: companyId,
                CompanyName: selectedCompany.CompanyName || selectedCompany.companyName || "",
                Address: selectedCompany.Address || selectedCompany.address || "",
                MobileNo: selectedCompany.MobileNo || selectedCompany.mobileNo || "",
                GSTNo: selectedCompany.GSTNo || selectedCompany.gstNo || "",
                EmailId: selectedCompany.EmailId || "",
                Website: selectedCompany.Website || "",
                imageBase64: imageBase64 || null,
            } : null;

            console.log(companyHeader)

            // Step 7: Call production PDF API
            console.log("[Production PDF] Sending to PDF backend...");
            const pdfResp = await axios.post(
                `${PDF_API_BASE}/api/production-pdf/${encodeURIComponent(prodMastID)}`,
                {
                    docId: prodMastID,
                    data: pdfData,
                    company: companyHeader
                },
                { responseType: "blob" }
            );

            const blob = pdfResp.data;
            if (!blob || blob.size === 0) throw new Error("Empty PDF");

            const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));

            // Step 8: Open/display PDF
            // Check if running in React Native WebView
            if (window.ReactNativeWebView) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: "OPEN_PDF",
                            base64: reader.result
                        })
                    );
                };
                reader.readAsDataURL(blob);
            } else {
                // Web browser - open in new tab
                const newWin = window.open(blobUrl, "_blank");
                if (newWin) {
                    newWin.focus();
                    const timer = setInterval(() => {
                        if (newWin.closed) {
                            URL.revokeObjectURL(blobUrl);
                            clearInterval(timer);
                        }
                    }, 1000);
                } else {
                    setSnackbar({
                        open: true,
                        message: "Popup blocked – PDF download may have been triggered",
                        severity: "warning"
                    });
                }
            }

            setSnackbar({
                open: true,
                message: "Production PDF generated successfully!",
                severity: "success"
            });

        } catch (err) {
            console.error("[Production PDF] Error:", err);
            setSnackbar({
                open: true,
                message: `Failed to generate Production PDF: ${err.message || err.response?.data?.error || 'Unknown error'}`,
                severity: "error"
            });
        } finally {
            setPdfLoading(false);
            setPdfLoadingId(null);
            setPdfOverlayVisible(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // DELETE HANDLER
    // ─────────────────────────────────────────────────────────────────────────────
    const handleDelete = (row) => {
        const ids = selectionModel.length > 0 ? selectionModel : [row.id];

        if (!ids.length) return;

        setPendingDeleteIds(ids);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        const numericIds = pendingDeleteIds.map(Number).filter(n => !isNaN(n));

        if (!numericIds.length) return;

        setOpenDeleteDialog(false);

        try {
            const { data } = await axios.post(
                `${API_URL}/delete-production`,
                {
                    objAryId: numericIds,
                    companyId
                }
            );

            const deletedIds = Array.isArray(data.deletedIds)
                ? data.deletedIds.map(Number)
                : [];

            if (deletedIds.length > 0) {
                setRows(prev => prev.filter(row => !deletedIds.includes(Number(row.id))));
                setSelectionModel(prev => prev.filter(id => !deletedIds.includes(Number(id))));
            }

            setSnackbar({
                open: true,
                message: 'Deleted successfully.',
                severity: 'success'
            });

        } catch (error) {
            console.error(error.response?.data?.error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || "Delete failed due to an unexpected error.",
                severity: "error"
            });
        } finally {
            setPendingDeleteIds([]);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // FETCH PRODUCTION LIST
    // ─────────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const res = await axios.get(`${API_URL}/getProduction-list`, {
                    params: { userId, userCategory, companyId }
                });

                const data = Array.isArray(res.data) ? res.data : [];
                console.log("[Production List] Data:", data);

                const mapped = data.map((row) => ({
                    id: row.ProdMastID,
                    receiptDate: row.ReceiptDate
                        ? new Date(row.ReceiptDate).toLocaleDateString("en-GB")
                        : "",
                    receiptNum: row.ReceiptNum,
                    issueBy: row.IssueBy,
                    receivedBy: row.ReceivedBy,
                    totalItems: row.totalItems || 0,
                    totalAmount: row.totalAmount || 0,
                }));

                setRows(mapped);

            } catch (err) {
                console.error("[Production List] Error:", err);
                setSnackbar({
                    open: true,
                    message: `Failed to fetch production list: ${err.message}`,
                    severity: "error"
                });
            } finally {
                setLoading(false);
            }
        };

        if (companyId && userId && userCategory) {
            fetchData();
        }
    }, [companyId, userId, userCategory]);

    const filteredRows = rows.filter((row) =>
        [row.receiptNum, row.issueBy, row.receivedBy]
            .join(" ")
            .toLowerCase()
            .includes(searchText.toLowerCase())
    );

    // ─────────────────────────────────────────────────────────────────────────────
    // DATAGRID COLUMNS
    // ─────────────────────────────────────────────────────────────────────────────
    const columns = [
        { field: "receiptDate", headerName: "Receipt Date", flex: 1 },
        {
            field: "receiptNum",
            headerName: "Receipt No.",
            flex: 1.6,
            renderCell: ({ value, row }) => (
                <Typography onClick={() => handleView(row)} sx={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer', fontSize: "14px" }} >
                    {value}
                </Typography>
            ),
        },
        { field: "issueBy", headerName: "Issue By", flex: 1.4 },
        { field: "receivedBy", headerName: "Received By", flex: 1.4 },
        {
            field: "view",
            headerName: "View PDF",
            flex: 0.4,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                const thisId = params.row.id;
                const isThisLoading = pdfLoading && Number(pdfLoadingId) === Number(thisId);

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        {isThisLoading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <IconButton
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewClick(thisId);
                                }}
                                size="small"
                            >
                                <PrintIcon />
                            </IconButton>
                        )}
                    </Box>
                );
            }
        }
    ];

    // Add delete button column if not User role
    if (role !== "User") {
        columns.push({
            flex: 0.3,
            minWidth: 80,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: "center",
            renderCell: (params) => {
                const isRowSelected = selectionModel.includes(params.row.id);
                return (
                    <Tooltip title={isRowSelected ? "Delete Selected" : "Select row to enable"}>
                        <span>
                            <IconButton
                                color="error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleDelete(params.row);
                                }}
                                disabled={!isRowSelected}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            }
        });
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // SELECT ALL HANDLER
    // ─────────────────────────────────────────────────────────────────────────────
    const handleSelectAllVisible = () => {
        const visibleIds = filteredRows.map(row => row.id);

        const allSelected = visibleIds.every(id =>
            selectionModel.includes(id)
        );

        if (allSelected) {
            setSelectionModel(prev =>
                prev.filter(id => !visibleIds.includes(id))
            );
        } else {
            setSelectionModel(prev => [
                ...new Set([...prev, ...visibleIds])
            ]);
        }
    };

    const allVisibleSelected =
        filteredRows.length > 0 &&
        filteredRows.every(row => selectionModel.includes(row.id));

    // ─────────────────────────────────────────────────────────────────────────────
    // MOBILE CARD COMPONENT
    // ─────────────────────────────────────────────────────────────────────────────
    const MobileCard = ({ row }) => {
        const isSelected = selectionModel.includes(row.id);
        const isThisLoading = pdfLoading && Number(pdfLoadingId) === Number(row.id);

        const toggleSelection = (e) => {
            e.stopPropagation();
            setSelectionModel(prev =>
                prev.includes(row.id)
                    ? prev.filter(id => id !== row.id)
                    : [...prev, row.id]
            );
        };

        return (
            <Card
                onClick={() => handleView(row)}
                sx={{
                    mb: 1.8,
                    borderLeft: '5px solid #1976d2',
                    borderRadius: 2,
                    boxShadow: 2,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 }
                }}
            >
                <CardContent sx={{ py: 1.5 }}>

                    {/* Top Row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>

                        {/* Checkbox */}
                        {role !== "User" && (
                            <Checkbox
                                checked={isSelected}
                                onChange={toggleSelection}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                            />
                        )}

                        {/* Receipt Info */}
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                {row.receiptNum}
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "#777" }}>
                                {row.receiptDate}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Names Section */}
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "0.8rem" }}>
                            <strong>Issue By:</strong> {row.issueBy || "-"}
                        </Typography>

                        <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
                            <strong>Received By:</strong> {row.receivedBy || "-"}
                        </Typography>
                    </Box>

                    {/* Divider */}
                    <Divider sx={{ my: 1 }} />

                    {/* Bottom Row (PDF View) */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: "-1px", mb: "-5px" }}>
                        {isThisLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            <IconButton
                                color="primary"
                                sx={{
                                    p: 0.5,
                                    m: 0,
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewClick(row.id);
                                }}
                            >
                                <PrintIcon />
                            </IconButton>
                        )}
                    </Box>

                </CardContent>
            </Card>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ pt: 0.7, px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
                <Box sx={{ position: "absolute", left: 0 }}>
                    <Tooltip title="Add New Production">
                        <IconButton component={Link} to="/main/store-master" sx={{ color: "orange" }}>
                            <VscNewFile size={25} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography
                    variant="h5"
                    sx={{
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                        fontWeight: 600,
                        textAlign: "center",
                    }}>
                    {isSmall ? "Production" : "Production Detail"}
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <Box sx={{ p: { xs: 0.5, md: 0.5 } }}>

                    {isMobile ? (
                        <>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={{ mb: 1.5 }}
                            />
                            {role !== "User" && selectionModel.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 2,
                                        px: 2,
                                        py: 1.2,
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        boxShadow: 1
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={allVisibleSelected}
                                            onChange={handleSelectAllVisible}
                                            size="small"
                                        />
                                        <Typography sx={{ fontSize: "0.85rem", ml: 1 }}>
                                            {selectionModel.length} selected
                                        </Typography>
                                    </Box>

                                    <IconButton
                                        color="error"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete();
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            )}

                            {loading && <SkeletonLoader count={3} height={120} />}
                            {!loading && rows.length === 0 && <NoDataComponent />}
                            {!loading && filteredRows.length === 0 && <CustomNoDataOverlay />}
                            {!loading && filteredRows.length > 0 && filteredRows.map((row) => (
                                <MobileCard key={row.id} row={row} />
                            ))}
                        </>
                    ) : (
                        <Paper elevation={2} sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden' }}>
                            <Box sx={{ height: '78vh', width: '100%', overflow: 'hidden', position: 'relative' }}>
                                <LoadingOverlay isVisible={loading} />
                                {!loading && rows?.length === 0 && <NoDataComponent />}
                                {!loading && rows?.length > 0 && <DataGrid
                                        rows={filteredRows}
                                        columns={columns}
                                        getRowId={(row) => row.id}
                                        checkboxSelection={role !== "User"}
                                        rowSelectionModel={selectionModel}
                                        onRowSelectionModelChange={(newSel) => setSelectionModel(newSel.map(Number))}
                                        pageSizeOptions={[50, 100]}
                                        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                                        sx={{
                                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                            '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },
                                            '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                            '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },
                                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },
                                            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },
                                            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                        }}
                                        slots={{
                                            noResultsOverlay: CustomNoDataOverlay
                                        }}
                                    />}
                            </Box>
                        </Paper>
                    )}

                </Box>
            </Paper>

            {/* DELETE CONFIRMATION DIALOG */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete <strong>{pendingDeleteIds.length}</strong> record(s)?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* PDF LOADING OVERLAY */}
            {pdfOverlayVisible && (
                <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.92)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}>
                    <Box sx={{ textAlign: "center", backgroundColor: "#111", padding: "40px 60px", borderRadius: "20px", boxShadow: "0 20px 40px rgba(0,0,0,0.6)" }}>
                        <CircularProgress size={60} thickness={4} sx={{ color: "#fff" }} />
                        <Typography variant="h6" sx={{ color: "#fff", mt: 3, fontWeight: 600 }}>Generating PDF…</Typography>
                        <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>Please wait</Typography>
                    </Box>
                </Box>
            )}

            {/* SNACKBAR */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ whiteSpace: "pre-line" }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}